import { useMemo } from 'react';
import { css } from '@emotion/react';
import {
  Banner,
  Button,
  Card,
  color,
  Icon,
  spacing,
  ToggleButton,
  Typography
} from '@uniquegood/realworld-studio-design';
import { useParams } from 'react-router-dom';
import { ATagStyle, CardItemGap } from '@/styles';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { usePostRequest, usePromotionModal, useRequest } from '@/hooks';
import LoadingSpinner from '@/components/LoadingSpinner';
import { PromotionTriggerLocation } from '@/track';

function DonationBanner() {
  return (
    <Banner
      header="내가 만든 콘텐츠를 판매하고 싶으신가요?"
      type="default"
      cssStyle={css`
        margin-bottom: ${spacing.margin.xlarge4};
      `}
    >
      <Typography type="body">
        <a
          href="https://realworld-school.org/"
          target="_blank"
          rel="noreferrer"
          css={[
            ATagStyle,
            css`
              color: #282f89;
            `
          ]}
        >
          리얼월드 스쿨
        </a>
        에 내 콘텐츠를 업로드하고 수익을 창출해 보세요!
      </Typography>
    </Banner>
  );
}

export default function Donations() {
  const { plan } = usePlanLimits();
  const { appId } = useParams<AppParam>();
  const {
    data: { data: isDonationActive } = {},
    isValidating: isLoading,
    mutate: mutateDonation
  } = useRequest<CommonResponseModel<boolean>>(`/apps/${appId}/donation`);

  const { trigger: triggerDonationEnable } = usePostRequest(`/apps/${appId}/donation/enable`);
  const { trigger: triggerDonationDisable } = usePostRequest(`/apps/${appId}/donation/disable`);

  const isDonationsAvailable = useMemo(() => {
    return plan === 'Pro' || plan === 'Enterprise' || plan === 'Teacher' || plan === 'Realworld';
  }, [plan]);

  const showDonationBanner = useMemo(() => {
    return plan === 'Teacher' || plan === 'Class';
  }, [plan]);

  const handleToggleDonations = async () => {
    if (isDonationActive) {
      await triggerDonationDisable();
    } else {
      await triggerDonationEnable();
    }

    mutateDonation();
  };

  const { openPromotionModal } = usePromotionModal();

  const handleUpgradeClick = () => {
    openPromotionModal({
      recommendedPlan: plan === 'Free' ? 'Pro' : 'Enterprise',
      triggerLocation: PromotionTriggerLocation.donation
    });
  };

  if (isLoading && isDonationActive === undefined) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {showDonationBanner && <DonationBanner />}
      <Card
        header={{
          content: '후원 설정',
          prefix: <Icon icon="donate_solid" size="15px" />
        }}
        css={CardItemGap}
      >
        <ToggleButton
          checked={isDonationActive}
          onChange={handleToggleDonations}
          disabled={!isDonationsAvailable}
          cssStyle={css`
            margin-bottom: ${spacing.margin.medium};

            > span {
              color: ${!isDonationsAvailable ? '#adadad' : '#8f8f8f'};
            }

            > .indicator {
              background: ${!isDonationsAvailable ? '#cccccc' : '#8f8f8f'};
            }
          `}
        >
          후원 받기
        </ToggleButton>
        <Typography
          as="p"
          type="caption"
          css={css`
            color: #8f8f8f;
          `}
        >
          프로, 엔터프라이즈, 티처 요금제에서만 후원 받기 가능 (수수료 40%)
        </Typography>
        {plan === 'Free' && (
          <Button
            type="primary"
            size="medium"
            onClick={handleUpgradeClick}
            cssStyle={UpgradeButtonStyle}
          >
            요금제 업그레이드
          </Button>
        )}
      </Card>
    </>
  );
}

const UpgradeButtonStyle = css`
  margin-top: ${spacing.margin.xlarge2};
  align-self: flex-start;
  background: linear-gradient(90deg, #7800ff 0%, #829bff 100%);
  border: none;
  border-radius: ${spacing.borderRadius.medium};
  padding: ${spacing.padding.small} ${spacing.padding.medium};
  color: ${color.white};
  font-weight: 500;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;
